import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LogoWhite from '../components/LogoWhite';
import HeadlineCopy from '../components/HeadlineCopy';
import EnhancedInputAvatar from '../components/EnhancedInputAvatar';
import InteractiveCarousel from '../components/InteractiveCarousel';
import Footer from '../components/Footer';
import Toast, { ToastType } from '../components/Toast';
// import PoppingModal from '../components/PoppingModal';
import OutOfCreditsModal from '../components/OutOfCreditsModal';
import LoadingScreen from '../components/LoadingScreen';
import { usePoppingModal } from '../hooks/usePoppingModal';
import { useOutOfCreditsModal } from '../hooks/useOutOfCreditsModal';
import { FilterState, AvatarGeneration, AppState } from '../types/api';
import { localStorageManager } from '../utils/localStorage';
import { apiService, ApiServiceError } from '../services/apiService';
import { createErrorInfo } from '../components/ErrorState';
import { mixpanelService } from '../services/mixpanelService';

function HomePage() {
  const navigate = useNavigate();
  const [isMobileView, setIsMobileView] = useState(false);
  // Modal state
  // const { isOpen: isModalOpen, closeModal: closeSignupModal } = usePoppingModal();
  // Unused - kept for future use: openSignupModal
  const { isOpen: isOutOfCreditsOpen, openModal: openOutOfCreditsModal, closeModal: closeOutOfCreditsModal, handleBuyCredits } = useOutOfCreditsModal();
  
  // Core app state
  const [currentPrompt, setCurrentPrompt] = useState('');
  const [currentFilters, setCurrentFilters] = useState<FilterState>({
    style: {
      type: 'photorealistic',
      ratio: 'vertical',
      scene: 'mid-closeup'
    },
    age: 'any',
    gender: 'any',
    hair: [],
    background: 'any',
    model: 'nano-banana'
  });
  
  // Enhanced toast state
  const [toastMessage, setToastMessage] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [toastType, setToastType] = useState<ToastType>('success');
  const [toastTitle, setToastTitle] = useState<string | undefined>();
  const [toastPersistent, setToastPersistent] = useState(false);
  //const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadedFileBase64, setUploadedFileBase64] = useState<string | null>(null);
  
  // LocalStorage state management
  const [appState, setAppState] = useState<AppState>({
    generationHistory: [],
    userPreferences: {
      style: {
        type: 'photorealistic',
        ratio: 'vertical',
        scene: 'mid-closeup'
      },
      age: 'any',
      gender: 'any',
      hair: [],
      background: 'any',
      model: 'nano-banana'
    },
    lastUpdated: new Date().toISOString()
  });
  const [showHistory, setShowHistory] = useState(false);
  const [isStateLoaded, setIsStateLoaded] = useState(false);
  
  // Generation state with enhanced error handling
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);

  // Load state from localStorage on app start
  useEffect(() => {
    const checkMobile = () => {
      setIsMobileView(window.innerWidth < 1024); // lg breakpoint in Tailwind
    };
    
    checkMobile();

    const loadAppState = async () => {
      try {
        // Migrate any old data format if needed
        localStorageManager.migrateData();
        
        // Load complete app state
        const savedState = localStorageManager.getAppState();
        
        // Initialize rate limit status if not present (mock data for development)
        if (!savedState.rateLimitStatus) {
          savedState.rateLimitStatus = {
            remainingUses: 15,
            usageCount: 0,
            resetDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // Next week
            isBlocked: false,
            weekStart: new Date().toISOString()
          };
        }
        
        setAppState(savedState);
        
      } catch (error) {
        console.error('Failed to load app state:', error);
        // Use default state if loading fails
      } finally {
        setIsStateLoaded(true);
      }
    };
    
    loadAppState();
  }, []);

  // Save state to localStorage whenever it changes
  useEffect(() => {
    if (isStateLoaded) {
      localStorageManager.saveAppState(appState);
    }
  }, [appState, isStateLoaded]);

  const handlePromptChange = (prompt: string) => {
    setCurrentPrompt(prompt);
  };

  const handleFiltersChange = (filters: FilterState) => {
    setCurrentFilters(filters);
  };

  const handleFileChange = (file: File | null, base64: string | null) => {
    setUploadedFileBase64(base64);
  };

  const handleAvatarSelect = (prompt: string, avatarFilters: Partial<FilterState>) => {
    // Set the prompt
    setCurrentPrompt(prompt);
    
    // Merge avatar filters with defaults (reset undefined fields to 'any' or [])
    const updatedFilters: FilterState = {
      gender: avatarFilters.gender || 'any',
      age: avatarFilters.age || 'any',
      hair: Array.isArray(avatarFilters.hair) ? avatarFilters.hair : (avatarFilters.hair ? [avatarFilters.hair] : []),
      background: avatarFilters.background || 'any',
      style: {
        type: avatarFilters.style?.type || 'photorealistic',
        ratio: avatarFilters.style?.ratio || 'vertical',
        scene: avatarFilters.style?.scene || 'mid-closeup'
      }
    };
    
    setCurrentFilters(updatedFilters);
  };

  // Enhanced toast management functions
  const showToastMessage = (message: string, type: ToastType = 'success', title?: string, persistent = false) => {
    setToastMessage(message);
    setToastType(type);
    setToastTitle(title);
    setToastPersistent(persistent);
    setShowToast(true);
  };

  const handleToastClose = () => {
    setShowToast(false);
    setToastTitle(undefined);
    setToastPersistent(false);
  };

  const handleGenerate = async (isRetry = false) => {
    // Validate prompt
    if (!currentPrompt.trim()) {
      showToastMessage('Please enter a prompt for your avatar.', 'warning', 'Missing Prompt');
      return;
    }

    // Check rate limit before starting
    if (appState.rateLimitStatus?.isBlocked || appState.rateLimitStatus?.remainingUses === 0) {
      openOutOfCreditsModal();
      return;
    }
    
    // Track generate-start event
    mixpanelService.trackGenerateStart(
      currentPrompt,
      currentFilters,
      !!uploadedFileBase64,
      false // hasResults: false for HomePage
    );
    
    setIsGenerating(true);
    setGenerationProgress(0);
    
    // Start 15-second progress timer
    const startTime = Date.now();
    const progressInterval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const calculatedProgress = Math.min((elapsed / 18000) * 99, 99);
      setGenerationProgress(calculatedProgress);
      
      if (calculatedProgress >= 99) {
        clearInterval(progressInterval);
      }
    }, 50); // Update every 50ms for smooth animation
    
    try {
      
      // Make real API call to generate avatars (runs in parallel with progress timer)
      const response = await apiService.generateAvatars(
        currentPrompt,
        currentFilters,
        uploadedFileBase64 || undefined
      );
      
      if (!response.success || !response.data) {
        throw new ApiServiceError('Generation failed', 500, 'GENERATION_ERROR');
      }
      
      // Wait for progress to reach 99% if it hasn't already
      const currentProgress = (Date.now() - startTime) / 18000 * 99;
      if (currentProgress < 99) {
        await new Promise(resolve => {
          const remainingTime = 18000 - (Date.now() - startTime);
          setTimeout(resolve, remainingTime);
        });
      }
      
      // Clear the interval and set to 100%
      clearInterval(progressInterval);
      setGenerationProgress(100);
      
      // Create real generation object from API response
      const realGeneration: AvatarGeneration = {
        id: response.data.generationId,
        prompt: response.data.promptInfo.original,
        enhancedPrompt: response.data.promptInfo.enhanced,
        filters: currentFilters,
        s3Urls: response.data.s3Urls,
        timestamp: new Date().toISOString(),
        modelUsed: 'FLUX Model (Production)',
        usedEnhancement: response.data.promptInfo.usedEnhancement
      };
      
      // Update rate limit from API response
      const updatedRateLimit = {
        remainingUses: response.data.remainingUses,
        usageCount: response.data.usageCount,
        resetDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
        isBlocked: response.data.remainingUses === 0,
        weekStart: new Date().toISOString()
      };
      
      // Save both generation and rate limit atomically (single operation)
      localStorageManager.saveGenerationWithRateLimit(realGeneration, updatedRateLimit);

      // Update local state
      setAppState(localStorageManager.getAppState());
      
      // Show success message with usage info
      const remainingText = updatedRateLimit.remainingUses > 0 
        ? `${updatedRateLimit.remainingUses} generation${updatedRateLimit.remainingUses === 1 ? '' : 's'} remaining this week.`
        : 'Weekly limit reached. Resets on Sunday.';
      
      showToastMessage(
        `Successfully generated ${realGeneration.s3Urls.length} avatars! ${remainingText}`,
        'success',
        'Generation Complete'
      );
      
      // Navigate to results page with generation data
      navigate('/results', { 
        state: { 
          generation: realGeneration,
          rateLimitStatus: updatedRateLimit 
        }
      });
      
    } catch (error) {
      console.error('Generation failed:', error);
      
      // Clear the interval on error
      clearInterval(progressInterval);
      
      // Create comprehensive error info
      const errorInfo = createErrorInfo(error);
      
      // Handle rate limit errors with modal, others with toast
      if (errorInfo.type === 'rate_limit') {
        openOutOfCreditsModal();
      } else {
        showToastMessage(
          errorInfo.message,
          'error',
          'Generation Failed',
          false
        );
      }
      
    } finally {
      setIsGenerating(false);
    }
  };

  // Generation History handlers
  // Unused - kept for future use
  // const handleSelectGeneration = (generation: AvatarGeneration) => {
  //   // Load the selected generation
  //   setCurrentPrompt(generation.prompt);
  //   setCurrentFilters(generation.filters);
  //   setShowHistory(false);
  //   
  //   // Show success message
  //   showToastMessage(
  //     `Loaded generation: "${generation.prompt.substring(0, 30)}${generation.prompt.length > 30 ? '...' : ''}"`,
  //     'success',
  //     'Generation Loaded'
  //   );
  //   
  // };

  // Unused - kept for future use
  // const handleClearHistory = () => {
  //   // localStorageManager.clearGenerationHistory();
  //   // setAppState({
  //   //   ...appState,
  //   //   generationHistory: [],
  //   //   lastGenerationId: undefined
  //   // });
  //   // setShowHistory(false);
  //   // showToastMessage('Generation history cleared', 'info', 'History Cleared');
  // };

  const handleToggleHistory = () => {
    setShowHistory(!showHistory);
  };

  // Development helper: Reset rate limit for testing
  const handleResetRateLimit = async () => {
    try {
      // Call backend to reset rate limit
      await apiService.resetRateLimit();
      
      // Update local state
      const resetRateLimit = {
        remainingUses: 15,
        usageCount: 0,
        resetDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
        isBlocked: false,
        weekStart: new Date().toISOString()
      };
      
      setAppState({
        ...appState,
        rateLimitStatus: resetRateLimit
      });
      
      localStorageManager.saveRateLimitStatus(resetRateLimit);
      
      showToastMessage('Rate limit reset! You have 15 generations available.', 'success', 'Rate Limit Reset');
      
    } catch (error) {
      console.error('Failed to reset rate limit:', error);
      showToastMessage(
        'Failed to reset rate limit. Please try again.',
        'error',
        'Reset Failed'
      );
    }
  };

  // Show loading screen when generating
  if (isGenerating) {
    return (
      <LoadingScreen 
        progress={generationProgress}
        prompt={currentPrompt}
        filters={currentFilters}
        rateLimitStatus={appState.rateLimitStatus}
        hasUploadedFile={!!uploadedFileBase64}
        model={currentFilters.model}
      />
    );
  }

  // Render Home Page
  return (
    <div className="min-h-screen relative" style={{
      background: 'linear-gradient(to left, #f4fbfe 2.372%, #f6f8ff 99.351%)'
    }}>
      {/* Radial Blue Fade Overlay - Updated to match Figma design */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse 1200px 800px at 50% 50%, rgba(68, 129, 253, 0.4) 0%, rgba(68, 129, 253, 0.25) 30%, rgba(68, 129, 253, 0.01) 60%, transparent 70%)'
        }}
      />
      {/* Main Container */}
      <div className="relative w-full mx-auto min-h-screen">
        {/* Header - Responsive */}
        <div className="relative z-10 max-w-[1440px] mx-auto">
          {/* Mobile Layout */}
          <div className="lg:hidden">
            {/* Mobile Header - Three column layout */}
            <div className="flex items-center pt-4 px-4 pb-2">
              {/* Left spacer for centering logo */}
              <div className="flex-1"></div>
              {/* Centered Logo */}
              <div className="flex-1 flex justify-center">
                <LogoWhite />
              </div>
              
              {/* Right side - Auth buttons */}
              <div className="flex-1 flex justify-end items-center gap-3">
              </div>
            </div>
          </div>
          
          {/* Desktop Layout */}
          <div className="hidden lg:flex justify-between items-center pt-6 px-6">
            {/* Left spacer for centering logo */}
            <div className="flex-1"></div>
            
            {/* Centered Logo */}
            <div className="flex-1 flex justify-center">
              <LogoWhite />
            </div>
            
            {/* Right side - Auth buttons */}
            <div className="flex-1 flex justify-end items-center gap-5">
            </div>
          </div>
        </div>

        {/* Development Tools (Temporary) */}
        <div className="relative px-6 mb-6 space-y-4">
          {/* Rate Limit Reset Button - Development Only */}
          {process.env.REACT_APP_SHOW_DEV_TOOLS === 'true' && (
            <div className="flex justify-center">
              <button
                onClick={handleResetRateLimit}
                className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg font-medium text-sm transition-colors"
                title="Reset rate limit for testing"
              >
                🔄 Reset Rate Limit (Dev)
              </button>
            </div>
          )}
        </div>

        {/* Main Content - Responsive Layout */}
        <div className="relative flex flex-col items-center px-4 lg:px-0">
          {/* Headline - Responsive */}
          <div className="mt-6 lg:mt-8 mb-6 lg:mb-8 w-full max-w-[400px] lg:max-w-none text-center">
            <HeadlineCopy />
          </div>

          {/* Enhanced Input Avatar - Responsive */}
          <div className="mb-6 lg:mb-8 w-full lg:max-w-none">
            <EnhancedInputAvatar
              onPromptChange={handlePromptChange}
              onFiltersChange={handleFiltersChange}
              onFileChange={handleFileChange}
              initialPrompt={currentPrompt}
              initialFilters={currentFilters}
              onGenerate={handleGenerate}
              isGenerating={isGenerating}
              rateLimitStatus={appState.rateLimitStatus}
              disabled={isGenerating}
              hasHistory={isStateLoaded && appState.generationHistory.length > 0}
              onHistoryToggle={handleToggleHistory}
              isHistoryVisible={showHistory}
              isMobileView={isMobileView}
              hasResults={false} // hasResults: false for HomePage
            />
          </div>
        </div>

        {/* Interactive Carousel - Full Width, Edge-to-Edge */}
        <div className="mt-4 lg:mt-8 mb-8 lg:mb-16 w-full">
          <InteractiveCarousel onAvatarSelect={handleAvatarSelect} />
        </div>

        {/* Footer - Responsive Layout */}
        <div className="relative flex flex-col items-center px-4 lg:px-0">
          <div className="mt-8 lg:mt-16 mb-8 w-full max-w-[343px] lg:max-w-none">
            <Footer />
          </div>
        </div>
      </div>

      {/* Toast Notification */}
      <Toast
        message={toastMessage}
        isVisible={showToast}
        onClose={handleToastClose}
        type={toastType}
        title={toastTitle}
        persistent={toastPersistent}
      />

      {/* Popping Modal for Sign Up */}
      {/* <PoppingModal
        isOpen={isModalOpen}
        onClose={closeSignupModal}
        title="Free Custom Avatar & AI Pro"
        subtitle="Subscribe to AI Pro to create a custom avatar from BIGVU's global catalog. Use on iPhone, Android, and Web."
        onSubscribe={() => {
          window.open('https://desk.bigvu.tv/welcome?utm_campaign=avatar_generator&utm_content=subscribe&utm_medium=modal&utm_source=prompt2avatar', '_blank');
          closeSignupModal();
        }}
        onAlreadySubscribed={() => {
          window.open('https://desk.bigvu.tv/login?utm_campaign=avatar_generator&utm_content=already_subscribed&utm_medium=modal&utm_source=prompt2avatar', '_blank');
          closeSignupModal();
        }}
        showTimeline={true}
      /> */}

      {/* Out of Credits Modal */}
      <OutOfCreditsModal
        isOpen={isOutOfCreditsOpen}
        onClose={closeOutOfCreditsModal}
        onBuyCredits={handleBuyCredits}
        title="You're out of credits!"
        subtitle="You've reached your weekly limit of 15 avatar generations. Subscribe to get unlimited access and create as many avatars as you want."
      />
    </div>
  );
}

export default HomePage;
